import React, { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import capitalize from 'lodash/capitalize';
import Lozenge from '@atlaskit/lozenge';
import { SimpleTag as Tag } from '@atlaskit/tag';
import { getEntitlementRouteCCPV2, isDeletionPendingEntitlement } from '@atlassian/cssxp-utils-entitlement';
import { isEnterpriseInstanceOffering } from '@css-xp/ppc-utils';
import {
  BillingStatus,
  CloudSubscription,
  EntitlementDetails,
  PricingPlan,
  ProvisioningStatus,
  SubscriptionScheduleAction,
} from '@css-xp/graphtypes/src/generated-types-new';
import { BilledExternallyTag } from '@css-xp/ppc-billed-externally';
import { BILLED_EXTERNALLY, STATUSPAGE_ADDON_PRODUCT_ID } from '@css-xp/ppc-constants';
import {
  ActiveLozenge,
  AddOnLozenge,
  AppLozenge,
  BilledElsewhereLozenge,
  CancellationPendingLozenge,
  CopyableText,
  DeactivatedLozenge,
  DeletedLozenge,
  DeletionPendingLozenge,
  SandboxLozenge,
  SuspendedLozenge,
  TrialLozenge,
  FutureDatedLozenge,
  InternalUsageLicensesLozenge,
} from '../../../copyable-text';
import EntitlementIcon from '../../../entitlement-icon/entitlement-icon';
import './cloud-product-detail-view.scss';

export interface CloudProductDetailViewProps {
  productId: string;
  productName: string | undefined | null;
  entitlementNumber?: string;
  edition?: string | undefined | null;
  billingStatus?: string | undefined | null;
  provisioningStatus?: string | undefined | null;
  trial?: boolean;
  cloudSiteId?: string | undefined | null;
  isSandbox?: boolean;
  isApp?: boolean;
  offeringPricingType?: string;
  productLink?: string | undefined;
  logoId?: string;
  testId?: string;
  scheduledChanges?: EntitlementDetails['scheduledChanges'];
  upgradeTrial?: CloudSubscription['upgradeTrial'];
  version?: number;
  shouldHideExtraInfo?: boolean;
  billedElsewhere?: boolean;
  pricingPlan?: PricingPlan | null;
  shouldTopAlignIcon?: boolean;
  shouldHideEntitlementDetails?: boolean;
  isOpenInNewTab?: boolean;
  isEntitlementNumberCopyable?: boolean;
  isAddOn?: boolean;
  isExternallyBilled?: boolean;
  isInternalUsageLicenseEntitlement?: boolean;
  isStatusPageAddOn?: boolean;
}

const NonBillable = ({ version }: { version?: number }): ReactElement => {
  if (version === 2) {
    return (
      <span className="non-billable-tag">
        <Tag text="Non-billable" />
      </span>
    );
  }

  return <>Non-billable</>;
};

const SandboxStatus = ({ version }: { version?: number }) => {
  if (version === 2) {
    return (
      <span className="entity-status sandbox-tag">
        <Tag text="Sandbox" />
      </span>
    );
  }

  return <SandboxLozenge />;
};

const PaidLozenge = () => (
  <span className="entity-status">
    <Lozenge appearance="success" testId="paid-type-lozenge">
      PAID
    </Lozenge>
  </span>
);

export const EntitlementStatus = ({
  billingStatus,
  provisioningStatus,
  trial,
  scheduledChanges,
  entitlement,
  upgradeTrial,
  billedElsewhere,
  isSandbox,
  version,
  isAddOn,
  isFutureDatedInactiveEntitlement,
  hideInactiveStatus = false,
  isInternalUsageLicenseEntitlement,
  isApp,
}: {
  billingStatus?: string | undefined | null;
  provisioningStatus?: string | undefined | null;
  trial?: boolean;
  scheduledChanges?: EntitlementDetails['scheduledChanges'];
  entitlement?: EntitlementDetails;
  upgradeTrial?: boolean;
  billedElsewhere?: boolean;
  isSandbox?: boolean;
  version?: number;
  isAddOn?: boolean;
  hideInactiveStatus?: boolean;
  isFutureDatedInactiveEntitlement?: boolean;
  isInternalUsageLicenseEntitlement?: boolean;
  isApp?: boolean;
}) => {
  const lozenge = [];

  if (billingStatus === BillingStatus.ACTIVE) {
    lozenge.push(<ActiveLozenge />);
  }
  if (!hideInactiveStatus && getDeactivatedLozenge(billingStatus, provisioningStatus)) {
    lozenge.push(<DeactivatedLozenge />);
  }
  if (isDeletionPendingEntitlement(entitlement)) {
    lozenge.push(<DeletionPendingLozenge />);
  }
  if (getDeletedLozenge(billingStatus, provisioningStatus)) {
    lozenge.push(<DeletedLozenge />);
  }
  if (billingStatus === BillingStatus.SUSPENDED) {
    lozenge.push(<SuspendedLozenge />);
  }
  if (trial && !upgradeTrial) {
    lozenge.push(<TrialLozenge />);
  }
  if (getCancellationPendingLozenge(billingStatus, scheduledChanges)) {
    lozenge.push(<CancellationPendingLozenge />);
  }
  if (billedElsewhere) {
    lozenge.push(<BilledElsewhereLozenge />);
  }
  if (isSandbox) {
    lozenge.push(<SandboxStatus version={version} />);
  }
  if (isAddOn) {
    lozenge.push(<AddOnLozenge />);
  }
  if (isFutureDatedInactiveEntitlement) {
    lozenge.push(<FutureDatedLozenge />);
  }
  if (isInternalUsageLicenseEntitlement) {
    lozenge.push(<InternalUsageLicensesLozenge />);
  }
  if (isApp) {
    lozenge.push(<AppLozenge />);
  }
  return <>{lozenge}</>;
};

export const getDeletedLozenge = (
  billingStatus: EntitlementDetails['billingStatus'],
  provisioningStatus: EntitlementDetails['provisioningStatus']
) => {
  const isBillingInactive = billingStatus === BillingStatus.INACTIVE;
  const isBillingDeleted = billingStatus === BillingStatus.DELETED;

  if (provisioningStatus) {
    return provisioningStatus === ProvisioningStatus.DEACTIVATED && isBillingInactive;
  }

  return isBillingDeleted;
};

const getDeactivatedLozenge = (
  billingStatus: EntitlementDetails['billingStatus'],
  provisioningStatus: EntitlementDetails['provisioningStatus']
) => {
  const isBillingInactive = billingStatus === BillingStatus.INACTIVE;

  if (provisioningStatus) {
    return provisioningStatus === ProvisioningStatus.SUSPENDED && isBillingInactive;
  }

  return isBillingInactive;
};

const getCancellationPendingLozenge = (
  billingStatus: EntitlementDetails['billingStatus'],
  scheduledChanges: EntitlementDetails['scheduledChanges']
) => {
  return (
    billingStatus === BillingStatus.ACTIVE &&
    scheduledChanges?.subscriptionScheduleAction === SubscriptionScheduleAction.CANCEL &&
    !scheduledChanges?.offering
  );
};

const EntitlementType = ({
  isSandbox,
  isApp,
  offeringPricingType = '',
  trial,
  version,
  isAddOn,
}: {
  isSandbox?: boolean;
  isApp?: boolean;
  offeringPricingType?: string;
  trial: boolean;
  version?: number;
  isAddOn?: boolean;
}) => {
  const lozenge = [];
  if (isSandbox) {
    lozenge.push(<SandboxStatus version={version} />);
  }
  if (isApp) {
    lozenge.push(<AppLozenge />);
  }
  if (!trial && offeringPricingType?.toLowerCase() === 'paid') {
    lozenge.push(<PaidLozenge />);
  }
  if (isAddOn) {
    lozenge.push(<AddOnLozenge />);
  }
  return <>{lozenge}</>;
};

export const CloudProductDetailView = ({
  productId,
  productName,
  entitlementNumber,
  edition,
  billingStatus,
  provisioningStatus,
  trial = false,
  cloudSiteId,
  isSandbox,
  isApp,
  offeringPricingType = '',
  productLink,
  logoId = '',
  testId = 'cloud-product-detail-view',
  scheduledChanges,
  upgradeTrial,
  version,
  billedElsewhere,
  // shouldHideExtraInfo is used to hide extra status and lozenge information
  shouldHideExtraInfo,
  pricingPlan,
  shouldTopAlignIcon,
  shouldHideEntitlementDetails,
  isOpenInNewTab,
  isEntitlementNumberCopyable,
  isAddOn,
  isExternallyBilled,
  isInternalUsageLicenseEntitlement,
  isStatusPageAddOn,
}: CloudProductDetailViewProps): JSX.Element => {
  const renderEntitlementIcon = () => {
    return (
      <EntitlementIcon
        logoId={logoId}
        productId={isStatusPageAddOn ? STATUSPAGE_ADDON_PRODUCT_ID : productId}
        isCloud={true}
        className="avatar medium"
      />
    );
  };

  const renderProductName = () => {
    return <span data-testid={productId}>{productName ?? 'Unnamed product'}</span>;
  };

  const renderExtraStatusAndLozenge = () => {
    if (shouldHideExtraInfo) {
      return null;
    }

    return (
      <>
        {!upgradeTrial && (
          <div className="margin-top edition-details">
            {edition && (
              <span className="entitlement-edition">
                {isEnterpriseInstanceOffering(edition) ? <NonBillable version={version} /> : edition}
              </span>
            )}
            <EntitlementType
              isSandbox={isSandbox}
              isApp={isApp}
              offeringPricingType={offeringPricingType}
              trial={trial}
              version={version}
              isAddOn={isAddOn}
            />
            <EntitlementStatus
              billingStatus={billingStatus ?? ''}
              provisioningStatus={provisioningStatus ?? ''}
              trial={trial}
              scheduledChanges={scheduledChanges}
              upgradeTrial={!!upgradeTrial}
              billedElsewhere={billedElsewhere}
              isInternalUsageLicenseEntitlement={isInternalUsageLicenseEntitlement}
            />
          </div>
        )}
        {renderExternallyBilledTag()}
        {upgradeTrial && (
          <>
            <div className="margin-top edition-details">
              <span className="entitlement-edition">{upgradeTrial.baseOfferingName}</span>{' '}
              <EntitlementStatus
                billingStatus={billingStatus ?? ''}
                provisioningStatus={provisioningStatus ?? ''}
                trial={false}
                scheduledChanges={scheduledChanges}
                upgradeTrial
                billedElsewhere={billedElsewhere}
                isInternalUsageLicenseEntitlement={isInternalUsageLicenseEntitlement}
              />
            </div>
            <div className="margin-top edition-details">
              <span className="entitlement-edition" data-testId>
                {upgradeTrial.offeringName}
              </span>{' '}
              <TrialLozenge />
            </div>
          </>
        )}
      </>
    );
  };

  const renderExternallyBilledTag = () => {
    if (isExternallyBilled) {
      return <BilledExternallyTag text={BILLED_EXTERNALLY} marginLeft="0" />;
    }
    return null;
  };

  const renderEntitlementDetails = () => {
    if (shouldHideEntitlementDetails) {
      return null;
    }

    const billingFrequency = pricingPlan?.billingFrequency;
    const pricingPlanName = pricingPlan?.type;

    return (
      <div className="subscription-cell-details-v2" data-testid="subscription-cell-details-v2">
        {edition && <span className="n-300">{edition}</span>}
        {billingFrequency && (
          <span className="n-300">
            {edition && ` |`} {capitalize(billingFrequency)}
          </span>
        )}
        {pricingPlanName && (
          <span className="n-300">
            {(edition || billingFrequency) && ` |`} {capitalize(pricingPlanName)}
          </span>
        )}
      </div>
    );
  };

  if (version === 2) {
    // Added for new design iteration: https://hello.atlassian.net/wiki/spaces/tintin/pages/3307081579
    return (
      <div
        id="cloud-product-detail-view-v2"
        className={classNames({
          'cloud-product-icons-on-top': shouldTopAlignIcon,
          'mt-4': version === 2 && (isEnterpriseInstanceOffering(edition) || isSandbox),
        })}
        data-testid={testId}
      >
        {renderEntitlementIcon()}
        <span className="name-details-container">
          <Link
            to={productLink ?? getEntitlementRouteCCPV2(productId, entitlementNumber, cloudSiteId)}
            onClick={(e) => e.stopPropagation()}
            className="avatar-container"
            {...(isOpenInNewTab && { target: '_blank' })}
          >
            {renderProductName()}
          </Link>
          {entitlementNumber && (
            <h6 className="entitlement-number">
              {isEntitlementNumberCopyable ? <CopyableText text={entitlementNumber} duration={0} /> : entitlementNumber}
            </h6>
          )}
          {renderExtraStatusAndLozenge()}
          {renderEntitlementDetails()}
        </span>
      </div>
    );
  }

  return (
    <div id="cloud-product-detail-view" data-testid={testId}>
      <Link
        to={productLink ?? getEntitlementRouteCCPV2(productId, entitlementNumber, cloudSiteId)}
        onClick={(e) => e.stopPropagation()}
        className="avatar-container"
        {...(isOpenInNewTab && { target: '_blank' })}
      >
        {renderEntitlementIcon()} {renderProductName()}
      </Link>
      {entitlementNumber && (
        <h6 className="entitlement-number">
          {isEntitlementNumberCopyable ? <CopyableText text={entitlementNumber} /> : entitlementNumber}
        </h6>
      )}
      {renderExtraStatusAndLozenge()}
    </div>
  );
};
